<script lang="ts" setup>import { ref as _ref } from 'vue';

import { useOrderStore } from '@consumer/services/order'
import OrderSummaryOrderItemPreview from '@consumer/modules/checkout/OrderSummaryOrderItemPreview.vue'
import OrderGiftContent from '@consumer/modules/checkoutCart/OrderGiftContent.vue'
import type { OrderGift, GiftSuggestion } from '@consumer/types/serializers'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons'
import V2ModalFullscreen from '@consumer/components/V2/V2ModalFullscreen.vue'
import { checkout } from '@consumer/api'
import GiftSuggestions from '@consumer/modules/shared/GiftSuggestions.vue'
import { cloneDeep } from '@shared/object'
import { trackEvent } from '@shared/tracking'
import Actions from './addedToCart/Actions.vue'

const cart = useOrderStore()
let lastAddedGift = _ref<OrderGift>()
let hideExtraSection = _ref(false)
let giftSuggestions = _ref<GiftSuggestion[]>()
let localFavorites = _ref<GiftSuggestion[]>()
let open = _ref(true)

onMounted(() => {
  lastAddedGift.value = cloneDeep(cart.lastAddedGift)
  cart.lastAddedGift = undefined
})

watch(
  () => lastAddedGift.value,
  () => {
    if (lastAddedGift.value) {
      checkout.suggestions<{
        hideExtraSection: boolean
        giftSuggestions: GiftSuggestion[]
        localFavorites: GiftSuggestion[]
      }>().then(
        (response) => {
          hideExtraSection.value = response.hideExtraSection
          giftSuggestions.value = response.giftSuggestions
          setTimeout(() => {
            localFavorites.value = response.localFavorites
          }, 300)
        },
      )
    }
  },
  { immediate: true },
)

function goToCart () {
  return checkout.cart({ visit: true })
}

function onClose () {
  trackEvent('click', 'cart-keep-shopping-close-x-button')
  open.value = false
}
</script>

<template>
  <V2ModalFullscreen
    id="added-to-cart-modal"
    name="added-to-cart-modal"
    fullscreenMobile
    fullscreen
    :open="open"
    @close="onClose"
  >
    <div class="px-2 flex flex-col gap-5 py-5 mx-auto max-w-md lg:max-w-screen-lg lg:-mt-2 <lg:pb-52">
      <Transition appear name="fade-and-grow">
        <div class="flex items-center gap-2 <lg:mt-4">
          <div class="flex-grow text-2xl font-semibold flex gap-2 items-center whitespace-nowrap">
            <GIcon
              :icon="faCircleCheck"
              color="green-base"
              size="24px"
            />
            Added to Cart!

            <div class="flex-grow flex <lg:justify-end">
              <GPromiseButton
                flat
                primary
                :label="`View cart (${cart.order.orderGifts.length})`"
                size="medium"
                name="add-to-cart-modal-view-cart"
                @click="goToCart"
              />
            </div>
          </div>

          <div class="flex gap-2 flex-row <lg:hidden">
            <Actions @continueShopping="open = false"/>
          </div>
        </div>
      </Transition>

      <Transition appear name="fade-and-grow">
        <div v-if="lastAddedGift" class="flex gap-2 items-center bg-surface-blue py-6 px-5 rounded-v2md">
          <div class="w-32 lg:w-48">
            <OrderSummaryOrderItemPreview :orderGift="lastAddedGift"/>
          </div>
          <div>
            <OrderGiftContent
              :orderGift="lastAddedGift"
              class="text-v2-secondary flex-1"
            />
          </div>
        </div>
      </Transition>

      <div
        class="
          lg:hidden
          flex gap-2 flex-col
          w-full
          rounded-v2md
          bg-white
        "
      >
        <Actions @continueShopping="open = false"/>
      </div>

      <Transition appear name="fade-in-out">
        <div v-if="!hideExtraSection && giftSuggestions?.length">
          <h3 class="text-xl mb-3">You might also be interested in</h3>
          <GiftSuggestions :giftSuggestions="giftSuggestions" gaTrackName="cart-recommendation-swiper"/>
        </div>
      </Transition>

      <Transition appear name="fade-in-out">
        <div v-if="!hideExtraSection && localFavorites?.length">
          <h3 class="text-xl mb-3">Favorites Nearby</h3>
          <GiftSuggestions :giftSuggestions="localFavorites" gaTrackName="cart-recommendation-swiper"/>
        </div>
      </Transition>
    </div>
  </V2ModalFullscreen>
</template>
